import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  OnDestroy
} from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DiscussionService } from '../store/discussion.service';
import { PostsService } from 'src/app/shared/ui/post/data/store/service';
import { AuthService } from 'src/app/core/services/auth.service';
import { ActivatedRoute } from '@angular/router';
import {
  filter,
  map,
  switchMap,
  tap,
  first,
  take,
  takeUntil
} from 'rxjs/operators';
import { Profile } from '../../../core/services/suggestions/suggestions.model';
import {
  Article,
  SummerProgram
} from '../../../core/services/eduniverse/eduniverse.model';
import { select, Store } from '@ngrx/store';
import {
  selectAfterSchoolPrograms,
  selectArticles,
  selectJobs,
  selectSummerPrograms,
  State as EduniverseState
} from '../../../core/services/eduniverse/eduniverse.reducer';
import {
  loadAfterSchool,
  loadArticles,
  loadJobs,
  loadSummerProgram
} from '../../../core/services/eduniverse/eduniverse.actions';
import { PostState } from 'src/app/shared/ui/post/data/model/state';
import {
  selectStatus,
  selectComments,
  selectCommentsStatus,
  selectCommentsCount,
  selectPosts,
  selectReplies
} from 'src/app/shared/ui/post/data/store/selector';
import {
  deletePost,
  likePost,
  complainePost,
  sharePost,
  updatePost,
  paginatePosts,
  addCommentToPost,
  deleteComment,
  likeComment,
  replyComment,
  loadCommentsForPost,
  loadPostsByGroupId,
  loadPostsByDiscussionId
} from 'src/app/shared/ui/post/data/store/actions';
import { IPost } from 'src/app/shared/models/post';
import { IMessage } from 'src/app/shared/components/message/message.component';
import { ICommentForm } from 'src/app/shared/models/comment';
import { IPostsCtx } from '../../../shared/components/posts/list/posts.component';
import { Title, Meta } from '@angular/platform-browser';
import { MetaService } from 'src/app/shared/services/meta.service';
import { PlatformLocation } from '@angular/common';
import { SavedPostsFacade } from '../../../shared/stores/posts/saved/posts.saved.facade';
import { IDoc } from '../../../shared/models/doc';
import { RoutingService } from '../../../core/services/routing.service';
import { selectProfileInfo } from '../../profile/info/store/info.reducer';
import { PostsFacade } from 'src/app/modules/posts/store';
import { IDiscussion } from 'src/app/shared/models/discussion';

@Component({
  selector: 'app-discussion-container-bsr',
  templateUrl: './discussion-container.component.html',
  styleUrls: ['./discussion-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiscussionContainerBSRComponent implements OnInit, OnDestroy {
  renderer: any;

  discussionId$: Observable<any>;
  discussionPosts$: Observable<any>;

  summerPrograms$: Observable<{ totalCount: number; items: SummerProgram[] }>;
  afterSchoolPrograms$: Observable<{
    totalCount: number;
    items: SummerProgram[];
  }>;
  jobs$: Observable<{ totalCount: number; items: SummerProgram[] }>;
  articles$: Observable<{ totalCount: number; items: Article[] }>;

  readonly status$ = this.postStore.pipe(select(selectStatus()));

  private readonly _discussion$ = new BehaviorSubject<null | IDiscussion>(null);
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private eduniverseStore: Store<EduniverseState>,
    private service: DiscussionService,
    private postService: PostsService,
    readonly auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private postStore: Store<PostState>,
    private store: Store,
    private titleService: Title,
    private metaTagService: Meta,
    readonly savedPosts: SavedPostsFacade,
    readonly routing: RoutingService,
    private readonly postFacade: PostsFacade
  ) {
    // TODO: remove on destroy
    this.summerPrograms$ = this.eduniverseStore.pipe(
      select(selectSummerPrograms())
    );
    this.afterSchoolPrograms$ = this.eduniverseStore.pipe(
      select(selectAfterSchoolPrograms())
    );
    this.jobs$ = this.eduniverseStore.pipe(select(selectJobs()));
    this.articles$ = this.eduniverseStore.pipe(select(selectArticles()));
  }

  ngOnInit(): void {
    this.auth.token$
      .pipe(
        filter((_) => !!_),
        take(1),
        tap((_) => this.loadDataForAuthUser()),
        takeUntil(this._destroy$)
      )
      .subscribe((_) => _);

    this.discussionId$ = this.activatedRoute.params.pipe(
      filter((params) => params.hasOwnProperty('id')),
      map((param) => param.id),
      takeUntil(this._destroy$)
    );

    this.discussionId$
      .pipe(
        switchMap((id) => this.service.getDiscussionById(id)),
        tap((_) => this._discussion$.next(_)),
        takeUntil(this._destroy$)
      )
      .subscribe();

    this.discussionPosts$ = this.discussionId$.pipe(
      tap((id) => this.postStore.dispatch(loadPostsByDiscussionId({ id }))),
      switchMap(() => this.postStore.pipe(select(selectPosts({})))),
      takeUntil(this._destroy$)
    );
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get discussion$() {
    return this._discussion$;
  }

  get profileType$() {
    return this.auth.user$.pipe(map((_) => _?.type));
  }

  public openDoc(doc: IDoc) {
    this.routing.openDoc(doc);
  }

  likePost(id) {
    this.postService.likePost(id).subscribe((res) => {
      this._discussion$.value.post.likes.count = res.count;
    });
  }

  delete(id) {
    this.postService.deletePost(id).subscribe((res) => console.log('res', res));
  }

  complaine(id) {
    this.postService
      .complainePost(id)
      .subscribe((res) => console.log('res', res));
  }

  readonly postsCtx: IPostsCtx = {
    status: this.status$,
    delete: ({ id }) => this.postStore.dispatch(deletePost({ id })),
    like: ({ id }) => this.postStore.dispatch(likePost({ id })),
    complain: ({ id }) => this.postStore.dispatch(complainePost({ id })),
    share: ({ id }, message) => {
      this.postFacade.sharePost(id, message);
    },
    update: async (post, message) => {
      this.postFacade.updatePost(post.id, message);
    },
    paginate: (_posts, _page) => {
      // discussions do not provide pagination for posts
      // only one post would be loaded and a set of comments to it
    },
    save: (post) => {
      this.savedPosts.toggleSave(post);
    },
    modifyers: {
      forbidDelete: true
    },
    comments: {
      add: async (post, message) => {
        const form = await this.getCommentForm(post, message);
        this.postStore.dispatch(addCommentToPost({ comment: form }));
      },
      delete: (post, comment) => {
        console.log(comment);
        this.postStore.dispatch(deleteComment({ comment }));
      },
      like: (post, comment) => this.store.dispatch(likeComment({ comment })),
      reply: async (post, comment, message) => {
        const form = await this.getCommentForm(post, message);
        form['comment_id'] = comment.id;
        this.postStore.dispatch(replyComment({ comment: form }));
      },
      get: (post) => {
        return {
          values: this.store.pipe(select(selectComments(post.id))),
          status: this.store.pipe(select(selectCommentsStatus(post.id))),
          count: this.store.pipe(select(selectCommentsCount(post.id)))
        };
      },
      paginate: (post, comments) => {
        this.store.dispatch(
          loadCommentsForPost({ post, offset: comments.length })
        );
      },
      replies: {
        get: (_, comment) => this.store.select(selectReplies(comment.id))
      }
    }
  };

  protected async getCommentForm(
    post: IPost,
    message: IMessage
  ): Promise<ICommentForm> {
    return {
      post_id: post.id,
      text: message.text,
      foto: message.images,
      file: message.docs,
      links: message.linksToPreview
      // comment_id: comment.id,
    };
  }

  private loadDataForAuthUser() {
    this.eduniverseStore.dispatch(loadSummerProgram({ page: 1, term: '' }));
    this.eduniverseStore.dispatch(loadAfterSchool({ page: 1, term: '' }));
    this.eduniverseStore.dispatch(loadJobs({ page: 1, term: '' }));
    this.eduniverseStore.dispatch(
      loadArticles({ category: undefined, page: 1, term: '' })
    );
  }
}
